import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { Phone } from 'lucide-react';
import AdminPageHeader from '../../components/admin/AdminPageHeader';
import api from '../../api/client';

export default function PhoneSettings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [assignedNumbers, setAssignedNumbers] = useState([]);
  const [form, setForm] = useState({
    caller_id: '',
    forward_number: '',
    sms_signature: '',
    sms_enabled: true,
    voice_enabled: true,
  });

  const load = async () => {
    try {
      const { data } = await api.get('/admin/phone-config');
      setAssignedNumbers(data.assigned_numbers || []);
      setForm({
        caller_id: data.caller_id || '',
        forward_number: data.forward_number || '',
        sms_signature: data.sms_signature || '',
        sms_enabled: data.sms_enabled !== false,
        voice_enabled: data.voice_enabled !== false,
      });
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to load phone settings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put('/admin/phone-config', form);
      toast.success('Phone settings saved');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-3xl mx-auto p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/3" />
          <div className="h-40 bg-gray-200 rounded" />
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto">
      <AdminPageHeader
        title="Phone Settings"
        description="Configure your firm's phone and SMS behavior"
        icon={Phone}
        iconColor="text-indigo-600"
        iconBg="bg-indigo-50"
      />

      {/* Assigned numbers (read-only) */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 mb-6">
        <p className="text-sm font-semibold text-gray-900 mb-2">Your Assigned Number(s)</p>
        {assignedNumbers.length === 0 ? (
          <p className="text-sm text-gray-400 italic">No numbers assigned. Contact Sixty10 support to get a phone number.</p>
        ) : (
          <ul className="space-y-1">
            {assignedNumbers.map(n => (
              <li key={n} className="text-sm font-mono text-gray-800 bg-gray-50 px-3 py-1.5 rounded">{n}</li>
            ))}
          </ul>
        )}
      </div>

      {/* Editable config */}
      <form onSubmit={handleSave} className="bg-white rounded-xl border border-gray-200 p-4 space-y-4">
        <div>
          <label className="text-xs font-medium text-gray-600 mb-1 block">Caller ID shown to clients</label>
          <input type="text" placeholder="+19492980462"
            value={form.caller_id}
            onChange={e => setForm(p => ({ ...p, caller_id: e.target.value }))}
            className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-brand-500" />
          <p className="text-xs text-gray-500 mt-1">Your firm's public number — what clients see when you call them.</p>
        </div>
        <div>
          <label className="text-xs font-medium text-gray-600 mb-1 block">Inbound calls forward to</label>
          <input type="text" placeholder="+19492980462"
            value={form.forward_number}
            onChange={e => setForm(p => ({ ...p, forward_number: e.target.value }))}
            className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-brand-500" />
          <p className="text-xs text-gray-500 mt-1">Where inbound calls ring through to.</p>
        </div>
        <div>
          <label className="text-xs font-medium text-gray-600 mb-1 block">SMS signature</label>
          <input type="text" placeholder="- T5 Law Firm"
            value={form.sms_signature}
            onChange={e => setForm(p => ({ ...p, sms_signature: e.target.value }))}
            className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-brand-500" />
          <p className="text-xs text-gray-500 mt-1">Appended to all outbound SMS messages.</p>
        </div>

        <div className="flex items-center gap-6 pt-2">
          <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
            <input type="checkbox" checked={form.sms_enabled}
              onChange={e => setForm(p => ({ ...p, sms_enabled: e.target.checked }))}
              className="h-4 w-4 rounded border-gray-300" />
            SMS enabled
          </label>
          <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
            <input type="checkbox" checked={form.voice_enabled}
              onChange={e => setForm(p => ({ ...p, voice_enabled: e.target.checked }))}
              className="h-4 w-4 rounded border-gray-300" />
            Voice enabled
          </label>
        </div>

        <div className="flex justify-end">
          <button type="submit" disabled={saving}
            className="text-sm bg-brand-600 hover:bg-brand-700 text-white px-4 py-2 rounded-lg font-medium disabled:opacity-50">
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </form>
    </div>
  );
}
